import type { RuteroLuggageSelection, RuteroOfferOptionsSelection } from '@bandido/shared/types';

/**
 * Qué mostrarle al asesor sobre su selección de plazas: nada elegido, algunos
 * pasajeros cubiertos, o todos. Es solo DESCRIPCIÓN para el cartel: si una
 * selección vale o no la decide el OPERADOR al cotizarla
 * (`useOfferOptionsPreview` → `state: 'rejected'`), nunca una regla nuestra.
 *
 * La cobertura se pinta con los DATOS que publica el hub — `coversPassengers`
 * de cada plaza (butaca = 1, camarote privado = su capacidad, ya resuelto por
 * el hub) y `passengersToCover` (infantes descontados) —, sin interpretar
 * `kind`/`shared`/`capacity` ni mínimos de ocupación.
 */
export type AccommodationSelectionStatus = 'untouched' | 'partial' | 'covered';

export interface OfferOptionsPreviewDraft<TPassenger> {
  selection: RuteroOfferOptionsSelection;
  passengers?: TPassenger[];
}

/**
 * Foto completa que se debouncea antes de cotizar. Los verticales que cotizan
 * por distribución/composición no mandan pasajeros; los demás conservan el
 * arreglo exacto porque el proveedor puede usarlo como contexto del quote.
 */
export function offerOptionsPreviewDraft<TPassenger>(
  selection: RuteroOfferOptionsSelection,
  passengers: TPassenger[] | undefined,
): OfferOptionsPreviewDraft<TPassenger> {
  return {
    selection,
    ...(passengers ? { passengers } : {}),
  };
}

/**
 * Tours y Circuitos cotizan por sus estructuras comerciales dedicadas. Sus
 * pasajeros no integran ese request, por lo que editarlos no debe invalidar
 * una cotización idéntica ni hacer parpadear su autoridad.
 */
export function passengersForOfferOptionsPreview<TPassenger>(
  vertical: string,
  passengers: TPassenger[],
): TPassenger[] | undefined {
  return vertical === 'tours' || vertical === 'circuits'
    ? undefined
    : passengers;
}

/** CRM linkage is not provider quote input. Preserve identity when only it changes. */
export function stablePreviewPassengers<T extends { travelerId?: string; travelerSourceId?: string }>(
  passengers: T[] | undefined,
  previous: T[] | undefined,
): T[] | undefined {
  const next = passengers?.map((passenger) => {
    const copy = { ...passenger };
    delete copy.travelerId;
    delete copy.travelerSourceId;
    return copy;
  });
  return JSON.stringify(next) === JSON.stringify(previous) ? previous : next;
}

/** No cotiza la foto anterior mientras el nuevo contexto sigue en debounce. */
export function offerOptionsPreviewReady<TPassenger>(
  enabled: boolean,
  live: OfferOptionsPreviewDraft<TPassenger>,
  quoted: OfferOptionsPreviewDraft<TPassenger>,
): boolean {
  return enabled && live === quoted;
}

/** Pasajeros cubiertos por la selección: Σ `coversPassengers` (1 si el hub no lo publica). */
export function coveredPassengers({
  selectedIds,
  accommodations,
}: {
  /** Una entrada POR PLAZA: el id se repite una vez por plaza elegida. */
  selectedIds: string[];
  accommodations: readonly { id: string; coversPassengers?: number }[];
}): number {
  return selectedIds.reduce((sum, id) => {
    const option = accommodations.find((item) => item.id === id);
    return sum + (option?.coversPassengers ?? 1);
  }, 0);
}

export function accommodationSelectionStatus({
  selectedCount,
  covered,
  passengersToCover,
}: {
  selectedCount: number;
  covered: number;
  passengersToCover: number;
}): AccommodationSelectionStatus {
  if (selectedCount === 0) return 'untouched';
  return covered >= passengersToCover ? 'covered' : 'partial';
}

/**
 * La selección tal como VIAJA: al preview que la cotiza y al hold que la
 * reserva. UNA definición para los dos pedidos, porque el hold tiene que
 * mandar exactamente lo que se cotizó (el contrato del hub garantiza el
 * `quotedPrice` con las MISMAS `options`).
 *
 * Sin elección los campos no viajan: `[]` o `''` no significan "sin elección"
 * para el hub —la ausencia deja lo incluido por defecto de la oferta—, y son
 * transversales al vertical (el que publica opciones es el conector).
 */
export function offerOptionsSelectionPayload(selection: RuteroOfferOptionsSelection): RuteroOfferOptionsSelection {
  return {
    ...(selection.pickupId ? { pickupId: selection.pickupId } : {}),
    ...(selection.ticketQuantities?.length ? { ticketQuantities: selection.ticketQuantities } : {}),
    ...(selection.accommodationIds?.length ? { accommodationIds: selection.accommodationIds } : {}),
    ...(selection.ticketTypeId ? { ticketTypeId: selection.ticketTypeId } : {}),
    ...(selection.sessionId ? { sessionId: selection.sessionId } : {}),
    ...(selection.occupancy ? { occupancy: selection.occupancy } : {}),
    ...(selection.supplements?.length ? { supplements: selection.supplements } : {}),
    ...(selection.quoteToken ? { quoteToken: selection.quoteToken } : {}),
    ...(selection.optionalItemCodes?.length ? { optionalItemCodes: selection.optionalItemCodes } : {}),
  };
}

/**
 * Body interno del preview. `quoteRefreshId` separa generaciones del cache de
 * Bandido, pero queda fuera de la selección contractual que viaja a Rutero.
 */
export function offerOptionsPreviewRequestPayload({
  provenanceToken,
  selection,
  quoteRefreshId,
  distribution,
  passengers,
  partySize,
  luggage,
}: {
  provenanceToken: string | undefined;
  selection: RuteroOfferOptionsSelection;
  quoteRefreshId?: string;
  distribution?: { ages: number[] };
  passengers?: unknown[];
  partySize?: number;
  luggage?: RuteroLuggageSelection;
}) {
  return {
    provenanceToken,
    ...offerOptionsSelectionPayload(selection),
    ...(quoteRefreshId ? { quoteRefreshId } : {}),
    ...(distribution ? { distribution } : {}),
    ...(passengers?.length ? { passengers } : {}),
    ...(partySize ? { partySize } : {}),
    ...(luggage ? { luggage } : {}),
  };
}
